//Libs
import * as admin from "firebase/firestore";
import { z } from "zod";

//Local
import { dropUndefined } from "../utils/dropUndefined";
import { guardIsPlainObject } from "../utils/isPlainObject";
import { NotFoundError } from "../error-classes";
import { ExistingSiteKeyLocation } from "./site-key-location";
import {
  AccountingSyncStiltDoc,
  AccountingSyncStiltDocSchema,
} from "./accounting-sync";
import { zFallback } from "../utils/zod-fallback";

type DocumentData = admin.DocumentData;
type DocumentSnapshot = admin.DocumentSnapshot;
type TimestampType = admin.Timestamp;
const Timestamp = admin.Timestamp;

export interface PurchaseOrder {
  timestampCreated: TimestampType;
  createdBy: string;
  timestampLastModified: TimestampType;
  lastModifiedBy: string;
  vendorID: string;
  locationID: string;
  poNumber: string;
  vendorReferenceNumber: string | null;
  craftRecordID: string | null;
  issueDate: TimestampType;
  expectedDeliveryDate: TimestampType | null;
  paymentTerms: string | null;
  deliveryLocation: PODeliveryLocationValues;
  deliveryAddressLine1: string | null;
  deliveryAddressLine2: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZipCode: string | null;
  deliveryCountry: string | null;
  subTotal: number;
  totalAmount: number;
  totalTaxAmount: number;
  notes: string;
  internalNotes: string | null;
  lineItems: POLineItem[];
  status: POStatusValues;
  customData: { [key: string]: any };
  accountingSync?: AccountingSyncStiltDoc;
  priceBookItems?: string[];
}

export interface POLineItem {
  id: string;
  priceBookItemID: string | null;
  title: string;
  description: string | null;
  vendorPartNumber: string | null;
  quantity: number;
  quantityReceived: number;
  unitPrice: number;
  subTotal: number;
}

export interface POLineItemWithLocation extends POLineItem {
  location: ExistingSiteKeyLocation | null;
  purchaseOrder: ExistingPurchaseOrder;
}

export enum PODeliveryLocation {
  OFFICE = "Office",
  VENDOR = "Vendor",
  JOB_ADDRESS = "Job Address",
  OTHER = "Other",
}

/** prepended with O to denote "object" (as opposed to the Enum version) */
export const OPODeliveryLocation = {
  OFFICE: "Office",
  VENDOR: "Vendor",
  JOB_ADDRESS: "Job Address",
  OTHER: "Other",
} as const;

export type PODeliveryLocationValues =
  (typeof OPODeliveryLocation)[keyof typeof OPODeliveryLocation];

export function getReadablePODeliveryLocation(
  location: PODeliveryLocationValues,
): string {
  return location; // Already human-readable
}

/** Typeguard to determine if the given value is one of the PODeliveryLocationValues */
export function isValidPODeliveryLocationValue(
  value: string,
): value is PODeliveryLocationValues {
  const locationValues = Object.values(OPODeliveryLocation);
  return locationValues.includes(value as any);
}

export enum POStatus {
  DRAFT = "draft",
  PENDING = "pending",
  APPROVED = "approved",
  SENT = "sent",
  CANCELED = "canceled",
  PARTIALLY_RECEIVED = "partiallyReceived",
  COMPLETED = "completed",
}

/** prepended with O to denote "object" (as opposed to the Enum version) */
export const OPOStatus = {
  DRAFT: "draft",
  PENDING: "pending",
  APPROVED: "approved",
  SENT: "sent",
  CANCELED: "canceled",
  PARTIALLY_RECEIVED: "partiallyReceived",
  COMPLETED: "completed",
} as const;

export type POStatusValues = (typeof OPOStatus)[keyof typeof OPOStatus];

export function getReadablePOStatus(status: POStatusValues): string {
  switch (status) {
    case OPOStatus.DRAFT:
      return "Draft";
    case OPOStatus.PENDING:
      return "Pending";
    case OPOStatus.APPROVED:
      return "Approved";
    case OPOStatus.SENT:
      return "Sent";
    case OPOStatus.CANCELED:
      return "Canceled";
    case OPOStatus.PARTIALLY_RECEIVED:
      return "Partially received";
    case OPOStatus.COMPLETED:
      return "Completed";
    default:
      const _exhaustivenessCheck: never = status;
      return _exhaustivenessCheck;
  }
}

/** Typeguard to determine if the given value is one of the POStatusValues */
export function isValidPOStatusValue(value: string): value is POStatusValues {
  const statusValues = Object.values(OPOStatus);
  return statusValues.includes(value as any);
}

export interface ExistingPurchaseOrder extends PurchaseOrder {
  id: string;
  refPath: string;
}

/***********************/
// FOR THE API
/***********************/
type withoutTimestamps = Omit<
  PurchaseOrder,
  "timestampCreated" | "timestampLastModified"
>;

type withoutAllTimestamps = Omit<
  ExistingPurchaseOrder,
  | "timestampCreated"
  | "timestampLastModified"
  | "issueDate"
  | "expectedDeliveryDate"
>;

export interface ExistingPurchaseOrderISOTimestamps
  extends withoutAllTimestamps {
  timestampCreated: string;
  timestampLastModified: string;
  issueDate: string;
  expectedDeliveryDate: string | null;
}

type withoutPoNumber = Omit<withoutTimestamps, "poNumber">;

// Timestamps and poNumber are added on the backend.
export interface PO_CreateAPI extends withoutPoNumber {
  siteKey: string;
}

export type PO_UpdateAPI = Omit<
  Partial<ExistingPurchaseOrder>,
  "timestampCreated" | "createdBy"
>;

/** Utilities for interacting with PurchaseOrder objects  */
export const PurchaseOrderManager = {
  /** Drop `id` and `refPath` before saving to the database. Drop undefined */
  convertForFirestore: convertPurchaseOrderForFirestore,
  createFromFirestoreSnapshot: createFromFirestoreSnapshot,
  convertTimestampsToISO: convertTimestampsToISO,
  /** Use when validating something outgoing - writing to the DB or reading from the user */
  parse: validatePurchaseOrder,
  parseCreate: validatePurchaseOrder_Create,
  parseUpdate: validatePurchaseOrder_Update,
  /** Validate a PurchaseOrder doc, with fallbacks. Use for reading from the database. */
  parseWithFallbacks: validatePurchaseOrderWithFallbacks,
  /** Convert purchase order to a readable string format for LLM */
  convertForLLM,
} as const;

/** Drop `id` and `refPath` before saving to the database. Drop undefined values */
function convertPurchaseOrderForFirestore(
  purchaseOrder: ExistingPurchaseOrder | PO_UpdateAPI,
): DocumentData {
  const local = Object.assign({}, purchaseOrder);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, refPath, ...rest } = local;
  return dropUndefined(rest);
}

function createFromFirestoreSnapshot(
  snapshot: DocumentSnapshot,
): ExistingPurchaseOrder {
  if (!snapshot.exists) {
    throw new NotFoundError("Document does not exist.");
  }
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validatePurchaseOrderWithFallbacks(snapshot.data()),
  };
}

function convertTimestampsToISO(
  po: ExistingPurchaseOrder,
): ExistingPurchaseOrderISOTimestamps {
  const data: ExistingPurchaseOrderISOTimestamps = {
    vendorID: po.vendorID,
    poNumber: po.poNumber,
    craftRecordID: po.craftRecordID,
    locationID: po.locationID,
    deliveryAddressLine1: po.deliveryAddressLine1,
    deliveryAddressLine2: po.deliveryAddressLine2,
    deliveryCity: po.deliveryCity,
    deliveryState: po.deliveryState,
    deliveryZipCode: po.deliveryZipCode,
    deliveryCountry: po.deliveryCountry,
    deliveryLocation: po.deliveryLocation,
    id: po.id,
    vendorReferenceNumber: po.vendorReferenceNumber,
    paymentTerms: po.paymentTerms,
    lastModifiedBy: po.lastModifiedBy,
    lineItems: po.lineItems,
    notes: po.notes,
    internalNotes: po.internalNotes,
    refPath: po.refPath,
    status: po.status,
    subTotal: po.subTotal,
    createdBy: po.createdBy,
    timestampCreated: po.timestampCreated.toDate().toISOString(),
    timestampLastModified: po.timestampLastModified.toDate().toISOString(),
    issueDate: po.issueDate.toDate().toISOString(),
    expectedDeliveryDate:
      po.expectedDeliveryDate?.toDate().toISOString() ?? null,
    totalAmount: po.totalAmount,
    totalTaxAmount: po.totalTaxAmount,
    customData: po.customData,
  };

  if (po.priceBookItems) {
    data.priceBookItems = po.priceBookItems;
  }

  return data;
}

/* Zod validation schemas */
function validatePurchaseOrder(value: unknown): PurchaseOrder {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = purchaseOrderSchema.safeParse(value);
  if (!result.success) {
    throw new Error(`Invalid purchase order: ${result.error}`);
  }
  return result.data;
}

function validatePurchaseOrderWithFallbacks(value: unknown): PurchaseOrder {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = purchaseOrderSchemaWithFallbacks.parse(value);
  return result;
}

function validatePurchaseOrder_Create(value: unknown): PO_CreateAPI {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return poSchema_CreateAPI.parse(value);
}

function validatePurchaseOrder_Update(value: unknown): PO_UpdateAPI {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return poSchema_UpdateAPI.parse(value);
}

function convertForLLM(po: ExistingPurchaseOrder): string {
  const {
    id,
    poNumber,
    status,
    notes,
    vendorID,
    internalNotes,
    totalAmount,
    lineItems,
  } = po;

  const parts: string[] = [];

  // Add basic info
  parts.push(`This is a purchase order with an internal ID of ${id}`);

  // Add vendor name and PO number if present
  parts.push(
    `for vendor "${vendorID}"${poNumber ? ` with PO number ${poNumber}` : ""}`,
  );

  // Add status and amounts
  parts.push(`with status ${getReadablePOStatus(status)}`);
  parts.push(`for a total amount of $${totalAmount}`);

  // Add notes if present
  if (notes && notes.length > 0) {
    parts.push(`with notes: "${notes}"`);
  }

  // Add internal notes if present
  if (internalNotes && internalNotes.length > 0) {
    parts.push(`with internal notes: "${internalNotes}"`);
  }

  // Add line items count
  if (lineItems.length > 0) {
    // Make a nice readable string of line items
    const lineItemString = lineItems
      .map(
        (item) =>
          `${item.title} - ${item.description} - ${item.quantity} x $${item.unitPrice} = $${item.subTotal}`,
      )
      .join(", ");
    parts.push(`with line items: ${lineItemString}`);
  }

  return parts.join(" ");
}

// #region SECTION: Schemas
export const POLineItemSchema = z.object({
  id: z.string().min(1).max(200),
  priceBookItemID: z.string().min(1).max(200).nullable(),
  description: z.string().min(0).max(4000).nullable(),
  title: z.string().min(0).max(2000),
  quantity: z.number(),
  unitPrice: z.number(),
  subTotal: z.number(),
  vendorPartNumber: z.string().min(0).max(200).nullable(),
  quantityReceived: z.number(),
});

const purchaseOrderSchema = z.object({
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),
  createdBy: z.string().min(1).max(200),
  lastModifiedBy: z.string().min(1).max(200),
  vendorID: z.string().min(1).max(200),
  locationID: z.string().min(1).max(200),
  craftRecordID: z.string().min(1).max(200).nullable(),
  poNumber: z.string().min(1).max(200),
  vendorReferenceNumber: z.string().min(0).max(200).nullable(),
  status: z.nativeEnum(POStatus),
  issueDate: z.instanceof(Timestamp),

  deliveryLocation: z.nativeEnum(PODeliveryLocation),
  deliveryAddressLine1: z.string().min(0).max(200).nullable(),
  deliveryAddressLine2: z.string().min(0).max(200).nullable(),
  deliveryCity: z.string().min(0).max(200).nullable(),
  deliveryState: z.string().min(0).max(200).nullable(),
  deliveryZipCode: z.string().min(0).max(200).nullable(),
  deliveryCountry: z.string().min(1).max(200).nullable(),
  expectedDeliveryDate: z.instanceof(Timestamp).nullable(),

  paymentTerms: z.string().min(1).max(200).nullable(),

  subTotal: z.number(),
  totalAmount: z.number(),
  totalTaxAmount: z.number(),
  notes: z.string().min(0).max(8000),
  internalNotes: z.string().max(8000).nullable(),
  lineItems: z.array(POLineItemSchema).min(0),
  customData: z.record(z.any()),
  accountingSync: AccountingSyncStiltDocSchema.optional(),
  priceBookItems: z.array(z.string()).optional(),
});

const purchaseOrderSchemaWithFallbacks = z.object({
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),
  createdBy: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderSchemaWithFallbacks: 'createdBy'",
  ),
  lastModifiedBy: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderSchemaWithFallbacks: 'lastModifiedBy'",
  ),
  vendorID: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderSchemaWithFallbacks: 'vendorID'",
  ),
  locationID: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderSchemaWithFallbacks: 'locationID'",
  ),
  poNumber: zFallback(
    z.string().min(1).max(200),
    "--",
    "purchaseOrderSchemaWithFallbacks: 'poNumber'",
  ),
  vendorReferenceNumber: zFallback(
    z.string().min(0).max(200).nullable(),
    null,
    "purchaseOrderSchemaWithFallbacks: 'vendorReferenceNumber'",
  ),
  craftRecordID: zFallback(
    z.string().min(1).max(200).nullable(),
    null,
    "purchaseOrderSchemaWithFallbacks: 'craftRecordID'",
  ),
  status: zFallback(
    z.nativeEnum(POStatus),
    POStatus.DRAFT,
    "purchaseOrderSchemaWithFallbacks: 'status'",
  ),
  deliveryLocation: zFallback(
    z.nativeEnum(PODeliveryLocation),
    PODeliveryLocation.OFFICE,
    "purchaseOrderSchemaWithFallbacks: 'deliveryLocation'",
  ),
  deliveryAddressLine1: zFallback(
    z.string().min(0).max(200),
    "",
    "purchaseOrderSchemaWithFallbacks: 'deliveryAddressLine1'",
  ),
  deliveryAddressLine2: zFallback(
    z.string().min(0).max(200).nullable(),
    null,
    "purchaseOrderSchemaWithFallbacks: 'deliveryAddressLine2'",
  ),
  deliveryCity: zFallback(
    z.string().min(0).max(200).nullable(),
    "",
    "purchaseOrderSchemaWithFallbacks: 'deliveryCity'",
  ),
  deliveryState: zFallback(
    z.string().min(0).max(200).nullable(),
    null,
    "purchaseOrderSchemaWithFallbacks: 'deliveryState'",
  ),
  deliveryZipCode: zFallback(
    z.string().min(0).max(200).nullable(),
    "",
    "purchaseOrderSchemaWithFallbacks: 'deliveryZipCode'",
  ),
  deliveryCountry: zFallback(
    z.string().min(1).max(200).nullable(),
    "US",
    "purchaseOrderSchemaWithFallbacks: 'deliveryCountry'",
  ),
  issueDate: z.instanceof(Timestamp),
  expectedDeliveryDate: z.instanceof(Timestamp).nullable(),
  paymentTerms: zFallback(
    z.string().min(1).max(200).nullable(),
    null,
    "purchaseOrderSchemaWithFallbacks: 'paymentTerms'",
  ),
  subTotal: zFallback(
    z.number(),
    0,
    "purchaseOrderSchemaWithFallbacks: 'subTotal'",
  ),
  totalAmount: zFallback(
    z.number(),
    0,
    "purchaseOrderSchemaWithFallbacks: 'totalAmount'",
  ),
  totalTaxAmount: zFallback(
    z.number(),
    0,
    "purchaseOrderSchemaWithFallbacks: 'totalTaxAmount'",
  ),
  notes: zFallback(
    z.string().min(0).max(8000),
    "",
    "purchaseOrderSchemaWithFallbacks: 'notes'",
  ),
  internalNotes: zFallback(
    z.string().max(8000).nullable(),
    null,
    "purchaseOrderSchemaWithFallbacks: 'internalNotes'",
  ),
  lineItems: zFallback(
    z.array(POLineItemSchema).min(0),
    [],
    "purchaseOrderSchemaWithFallbacks: 'lineItems'",
  ),
  customData: zFallback(
    z.record(z.any()),
    {},
    "purchaseOrderSchemaWithFallbacks: 'customData'",
  ),
  accountingSync: AccountingSyncStiltDocSchema.optional(),
  priceBookItems: z.array(z.string()).optional(),
});

/***********************/
// FOR THE API
/***********************/
const withoutTimestampsSchema = purchaseOrderSchema.omit({
  timestampCreated: true,
  timestampLastModified: true,
});

// Used for interacting with the Create endpoint.
const poSchema_CreateAPI = withoutTimestampsSchema.extend({
  siteKey: z.string().min(1).max(400),
});

// Used for interacting with the Update endpoint
const poSchema_UpdateAPI = withoutTimestampsSchema
  .extend({
    id: z.string().min(1).max(200),
    refPath: z.string().min(1).max(400),
  })
  .partial();
// #endregion
